import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Spinner, CustomizedSnackbars } from "../../../../components";
import api from "../../../../helpers/api";
import { formatDate } from "../../utils/formatters";
import NumberFormat from "../../../../helpers/functions/numberFormat";

function ProductReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const transactionFromList = location.state?.transaction || null;

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSpinner, setIsSpinner] = useState(false);

  // Alert states
  const [isOpen, setIsOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");

  // Data states
  const [detailTransaction, setDetailTransaction] = useState(null);
  const [transactionSelected, setTransactionSelected] =
    useState(transactionFromList);

  // Fetch detail transaction
  useEffect(() => {
    const fetchDetailTransaction = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const res = await api.getDetailProductReceive(id);

        if (res.statusCode === 200) {
          console.log("detail receive", res.data);
          setDetailTransaction(res.data);
          if (!transactionSelected) {
            setTransactionSelected(res.data.transaction);
          }
        } else {
          showError(res.message);
        }
      } catch (error) {
        showError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetailTransaction();
  }, [id]);

  // Finish purchase order
  const handleFinishOrder = async () => {
    setIsSpinner(true);

    try {
      // Make sure every product has updatedQuantity set
      const updatedProducts = detailTransaction.attachments.map((item) => ({
        ...item,
        updatedQuantity: item.updatedQuantity || item.quantity,
      }));

      const res = await api.finishPurchaseOrder(
        { ...detailTransaction, attachments: updatedProducts },
        updatedProducts
      );

      if (res.statusCode === 200) {
        setIsSpinner(false);
        showSuccess("Purchase order finished successfully");

        // Navigate back to list after short delay
        setTimeout(() => {
          navigate("/ProductReceipt");
        }, 300);
      } else {
        showError(res.message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setIsSpinner(false);
    }
  };

  // Cancel purchase
  const handleCancelPurchase = async () => {
    setIsSpinner(true);

    try {
      const res = await api.cancelPurchases(detailTransaction);

      if (res.statusCode === 200) {
        setIsSpinner(false);
        showSuccess("Purchase order cancelled successfully");

        // Navigate back to list after short delay
        setTimeout(() => {
          navigate("/ProductReceipt");
        }, 300);
      } else {
        showError(res.message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setIsSpinner(false);
    }
  };

  // Utility functions
  const showError = (message) => {
    setIsOpen(true);
    setAlertType("error");
    setMessage(message);
  };

  const showSuccess = (message) => {
    setIsOpen(true);
    setAlertType("success");
    setMessage(message);
  };

  // Get status color
  const getStatusColor = (statusName) => {
    switch (statusName) {
      case "Done":
        return "success";
      case "Cancelled":
        return "error";
      case "Pending":
        return "warning";
      default:
        return "primary";
    }
  };

  // Check if order cannot be modified (completed or cancelled)
  const isOrderLocked = () => {
    return (
      detailTransaction?.statusName === "Done" ||
      detailTransaction?.statusName === "Cancel"
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Spinner isShowSpinner={isSpinner} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {/* Header with breadcrumbs */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                color="inherit"
                onClick={() => navigate("/ProductReceipt")}
                sx={{ cursor: "pointer", textDecoration: "none" }}
              >
                Product Receipt
              </Link>
              <Typography color="text.primary">Receipt Detail</Typography>
            </Breadcrumbs>

            <Typography variant="h6" sx={{ mt: 1 }}>
              Receipt #{detailTransaction?.purchaseNo || id}
            </Typography>
          </Box>

          {!isOrderLocked() && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancelPurchase}
                disabled={isOrderLocked()}
              >
                Cancel Order
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={handleFinishOrder}
                disabled={isOrderLocked()}
              >
                Finish Order
              </Button>
            </Stack>
          )}
        </Box>

        {detailTransaction ? (
          <>
            {/* Transaction Information */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Receipt Information
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Purchase Number
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailTransaction?.transactionNo || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Purchase Order Date
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(detailTransaction?.transactionDate)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Received Date
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailTransaction?.statusName === "Done"
                            ? formatDate(detailTransaction?.updatedDate)
                            : "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Branch
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailTransaction?.transactionToName || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Supplier
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailTransaction?.transactionFromName || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Chip
                          label={detailTransaction?.statusName}
                          color={getStatusColor(detailTransaction?.statusName)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Summary
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total Items
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {NumberFormat(
                            0,
                            detailTransaction?.attachments?.length || 0
                          ) || "0"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total Amount
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          Rp.{" "}
                          {NumberFormat(0, detailTransaction?.netPayment || 0)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Type
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailTransaction?.paymentType || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Note
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailTransaction?.note || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Products
            </Typography>

            <TableContainer>
              <Table aria-label="products table">
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Variant</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!detailTransaction.attachments ||
                  detailTransaction.attachments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No products available
                      </TableCell>
                    </TableRow>
                  ) : (
                    detailTransaction.attachments.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.variantName}</TableCell>
                        <TableCell>{item.productSku}</TableCell>
                        <TableCell>{NumberFormat(0, item.quantity)}</TableCell>
                        <TableCell>
                          Rp. {NumberFormat(0, item.trxPrice)}
                        </TableCell>
                        <TableCell>
                          Rp. {NumberFormat(0, item.netPrice)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total Section */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Card sx={{ minWidth: 300 }}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body2">
                        Rp. {NumberFormat(0, detailTransaction.netPayment || 0)}
                      </Typography>
                    </Grid>

                    {detailTransaction.discount > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">Discount:</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          <Typography variant="body2">
                            Rp. {NumberFormat(0, detailTransaction.discount)}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {detailTransaction.taxCharge > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">Tax:</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                          <Typography variant="body2">
                            Rp. {NumberFormat(0, detailTransaction.taxCharge)}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Grand Total:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Rp. {NumberFormat(0, detailTransaction.netPayment || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Receipt details not available
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/ProductReceipt")}
          >
            Back to Product Receipt
          </Button>
        </Box>
      </Paper>
    </>
  );
}

export default ProductReceiptDetail;
